import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { fetchHousingData } from '../api';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, LineChart, Line, CartesianGrid,
} from 'recharts';

// ── Colors ───────────────────────────────────────────────────────────────────
const C = {
  starts:   '#C8873A',   // copper — housing starts
  single:   '#C8873A',   // single-detached
  multi:    '#E8B98A',   // multiples (lighter copper)
  nonmarket: '#7C5CBF',  // purple — non-market housing
  vacancy:  '#378ADD',   // blue — vacancy / rental market
  shelter:  '#1D9E75',   // green — homelessness
};

// ── Formatters ────────────────────────────────────────────────────────────────
function fmt(n) {
  return Number(n).toLocaleString('en-CA');
}

function fmtShort(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${Math.round(n / 1_000)}k`;
  return String(n);
}

function fmtPct(n, decimals = 1) {
  if (n == null) return '—';
  return `${Number(n).toFixed(decimals)}%`;
}

function fmtChange(n, prefix = '') {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${prefix}${fmt(Math.round(n))}`;
}

function fmtPctChange(n) {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

// ── Stat sub-pill ─────────────────────────────────────────────────────────────
function StatPill({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="kicker text-ink-500" style={{ fontSize: 11 }}>{label}</span>
      <span className="font-serif text-ink-700" style={{ fontSize: 28, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );
}

// ── Change badge ──────────────────────────────────────────────────────────────
function ChangeBadge({ value, unit = '', positive = 'up' }) {
  if (value == null) return null;
  const isPos = value > 0;
  const isGood = (positive === 'up') ? isPos : !isPos;
  const color = isGood ? '#1D9E75' : '#D64949';
  const sign  = isPos ? '▲' : '▼';
  return (
    <span style={{ fontSize: 12, color, fontVariantNumeric: 'tabular-nums' }}>
      {sign} {Math.abs(value).toFixed(unit === '%' ? 2 : 0)}{unit} yr/yr
    </span>
  );
}

// ── Tooltip styles ─────────────────────────────────────────────────────────────
const TooltipBox = ({ children, label }) => (
  <div style={{
    background: '#1a1a2e',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6,
    padding: '10px 14px',
    fontSize: 13,
    color: '#e8e4da',
    minWidth: 200,
  }}>
    {label && <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>}
    {children}
  </div>
);

const TRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
    <span style={{ color: 'rgba(232,228,218,0.7)' }}>{label}</span>
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  </div>
);

// ── Custom tooltips ───────────────────────────────────────────────────────────
const StartsTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <TooltipBox label={label}>
      <TRow label="All starts"       value={fmt(d?.total)} />
      <TRow label="Single-detached"  value={fmt(d?.single)} />
      <TRow label="Multi-unit"       value={fmt(d?.multi)} />
    </TooltipBox>
  );
};

const VacancyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox label={label}>
      <TRow label="Vacancy rate" value={fmtPct(payload[0]?.value)} />
    </TooltipBox>
  );
};

const RentTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox label={label}>
      <TRow label="Avg monthly rent" value={`$${fmt(payload[0]?.value)}`} />
    </TooltipBox>
  );
};

// ── Section anchor nav pill ───────────────────────────────────────────────────
function SectionPill({ href, color, label }) {
  return (
    <a
      href={href}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cream-100/20 text-[13px] text-cream-100/70 hover:border-copper-500 hover:text-copper-400 transition"
    >
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
      {label}
    </a>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Housing() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    fetchHousingData()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const starts       = data?.starts;
  const nonmarket    = data?.nonmarket;
  const vacancy      = data?.vacancy;
  const avgRent     = data?.avg_rent;
  const homelessness = data?.homelessness;

  // Chart data
  const startsChart = (starts?.history ?? []).map(h => ({
    year:   String(h.year),
    total:  h.total,
    single: h.single,
    multi:  h.multi,
  }));

  const vacancyChart = (vacancy?.canada_history ?? []).map(h => ({
    year:         String(h.year),
    vacancy_rate: h.vacancy_rate,
  }));

  const rentChart = (avgRent?.canada_history ?? []).map(h => ({
    year:     String(h.year),
    avg_rent: h.avg_rent,
  }));

  // CMA combined table (join vacancy + rent by city)
  const cmaMap = {};
  (vacancy?.cma_table ?? []).forEach(r => {
    cmaMap[r.city] = { ...r };
  });
  (avgRent?.cma_rents ?? []).forEach(r => {
    if (cmaMap[r.city]) {
      cmaMap[r.city].avg_rent = r.avg_rent;
    } else {
      cmaMap[r.city] = { ...r };
    }
  });
  const cmaRows = Object.values(cmaMap).sort((a, b) =>
    (b.avg_rent ?? 0) - (a.avg_rent ?? 0)
  );

  return (
    <div className="min-h-screen bg-cream-100 text-ink-700">
      <Header />

      {/* ── Banner ── */}
      <section className="bg-ink-800 text-cream-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-cream-100/65 hover:text-copper-500 mb-6 transition"
          >
            <ArrowLeft size={14} /> Home
          </Link>
          <div className="kicker text-copper-500 mb-4">Housing</div>
          <h1 className="font-serif text-[56px] md:text-[64px] leading-[0.98] tracking-tight">
            Canadian housing<br />by the numbers
          </h1>
          <p className="mt-6 text-[16px] text-cream-100/75 max-w-2xl leading-relaxed">
            Annual housing starts, rental vacancy rates, average rents across major cities,
            and federal homelessness data — all drawn from Statistics Canada and federal open data.
          </p>

          <div className="flex flex-wrap gap-3 mt-10">
            <SectionPill href="#starts"       color={C.starts}   label="Housing starts" />
            <SectionPill href="#nonmarket"    color={C.nonmarket} label="Non-market housing" />
            <SectionPill href="#rental"       color={C.vacancy}  label="Rental market" />
            <SectionPill href="#homelessness" color={C.shelter}  label="Homelessness" />
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-16">

        {/* Loading */}
        {loading && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-8 animate-pulse">
            <div className="h-4 w-40 bg-ink-200 rounded mb-6" />
            <div className="h-20 w-64 bg-ink-200 rounded" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 mb-8 text-ink-500">
            Could not load live data — please try again shortly.
          </div>
        )}

        {/* ════════════════════════════════════════════════════
            SECTION 1 — HOUSING STARTS
        ════════════════════════════════════════════════════ */}
        <div id="starts" className="scroll-mt-8">

          {starts?.latest && (
            <div
              className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-4"
              style={{ borderLeft: `4px solid ${C.starts}` }}
            >
              <div className="kicker text-copper-600 mb-6">
                Housing starts · Canada · {starts.latest.year}
              </div>
              <div className="font-serif text-[72px] md:text-[100px] leading-[0.9] tracking-tight text-ink-800 tabular">
                {fmt(starts.latest.total)}
              </div>
              <div className="mt-3">
                <ChangeBadge value={starts.yoy_pct} unit="%" positive="up" />
                {starts.yoy_pct != null && (
                  <span className="text-[13px] text-ink-400 ml-2">vs {starts.prev?.year}</span>
                )}
              </div>

              <div className="flex flex-wrap gap-10 mt-8 pt-8 border-t border-ink-300/40">
                <StatPill
                  label="Single-detached"
                  value={fmt(starts.latest.single)}
                />
                <StatPill
                  label="Multi-unit"
                  value={fmt(starts.latest.multi)}
                />
                <StatPill
                  label="Multi-unit share"
                  value={starts.latest.total > 0
                    ? fmtPct(starts.latest.multi / starts.latest.total * 100, 0)
                    : '—'}
                />
              </div>
            </div>
          )}

          {/* Starts bar chart */}
          {startsChart.length > 0 && (
            <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-4">
              <h2 className="font-serif text-[22px] text-cream-100 mb-2">
                Housing starts over time
              </h2>
              <p className="text-[13px] text-cream-100/50 mb-8">
                Annual residential housing starts across Canada — single-detached vs multi-unit,{' '}
                {startsChart[0]?.year}–{startsChart.at(-1)?.year}
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={startsChart} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barCategoryGap="22%">
                  <XAxis
                    dataKey="year"
                    tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 12 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tickFormatter={n => fmtShort(n)}
                    tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                    axisLine={false} tickLine={false} width={44}
                  />
                  <Tooltip content={<StartsTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  <Bar dataKey="single" name="Single-detached" stackId="a" fill={C.single} radius={[0,0,0,0]} />
                  <Bar dataKey="multi"  name="Multi-unit"      stackId="a" fill={C.multi}  radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex gap-6 mt-5">
                <div className="flex items-center gap-2">
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: C.single, display: 'inline-block' }} />
                  <span className="text-[12px] text-cream-100/60">Single-detached</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: C.multi, display: 'inline-block' }} />
                  <span className="text-[12px] text-cream-100/60">Multi-unit (semi, row, apartment)</span>
                </div>
              </div>
            </div>
          )}

          {/* Starts historical table */}
          {starts?.history?.length > 0 && (
            <div className="overflow-x-auto mb-12">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-ink-300/60 text-ink-400 text-left">
                    <th className="pb-3 font-medium">Year</th>
                    <th className="pb-3 font-medium text-right">Total starts</th>
                    <th className="pb-3 font-medium text-right">Single-detached</th>
                    <th className="pb-3 font-medium text-right">Multi-unit</th>
                    <th className="pb-3 font-medium text-right">Multi-unit share</th>
                  </tr>
                </thead>
                <tbody>
                  {[...starts.history].reverse().map(row => (
                    <tr
                      key={row.year}
                      className={`border-b border-ink-200/60 ${row.year === starts.latest?.year ? 'font-semibold text-ink-800' : 'text-ink-600'}`}
                    >
                      <td className="py-2.5">{row.year}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.total)}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.single)}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.multi)}</td>
                      <td className="py-2.5 text-right tabular">
                        {row.total > 0 ? fmtPct(row.multi / row.total * 100, 0) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════
            SECTION 2 — NON-MARKET HOUSING STARTS
        ════════════════════════════════════════════════════ */}
        <div id="nonmarket" className="scroll-mt-8">

          {nonmarket ? (
            <>
              {/* Hero card */}
              <div
                className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-4"
                style={{ borderLeft: `4px solid ${C.nonmarket}` }}
              >
                <div className="kicker text-copper-600 mb-6">
                  Non-market housing starts · CMA Total · {nonmarket.period}
                </div>
                <div className="font-serif text-[72px] md:text-[100px] leading-[0.9] tracking-tight text-ink-800 tabular">
                  {fmt(nonmarket.total)}
                </div>
                <p className="text-[14px] text-ink-500 mt-2 max-w-xl">
                  Dwellings started across 18 major census metropolitan areas where units are not
                  priced or allocated through private market mechanisms.
                </p>

                {/* Tenure breakdown */}
                <div className="flex flex-wrap gap-10 mt-8 pt-8 border-t border-ink-300/40">
                  {nonmarket.rental > 0 && (
                    <StatPill label="Rental (non-market)" value={fmt(nonmarket.rental)} />
                  )}
                  {nonmarket.condo > 0 && (
                    <StatPill label="Non-market condo" value={fmt(nonmarket.condo)} />
                  )}
                  {nonmarket.coop > 0 && (
                    <StatPill label="Co-operative" value={fmt(nonmarket.coop)} />
                  )}
                  {nonmarket.homeowner > 0 && (
                    <StatPill label="Homeowner (non-profit)" value={fmt(nonmarket.homeowner)} />
                  )}
                </div>
              </div>

              {/* Tenure breakdown bar chart */}
              {nonmarket.total > 0 && (() => {
                const tenureData = [
                  { name: 'Rental',     value: nonmarket.rental },
                  { name: 'Condo',      value: nonmarket.condo },
                  { name: 'Co-op',      value: nonmarket.coop },
                  { name: 'Homeowner',  value: nonmarket.homeowner },
                ].filter(d => d.value > 0);

                return tenureData.length > 1 ? (
                  <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-4">
                    <h2 className="font-serif text-[22px] text-cream-100 mb-2">
                      Breakdown by tenure type
                    </h2>
                    <p className="text-[13px] text-cream-100/50 mb-8">
                      Non-market starts by tenure, all 18 CMAs combined · {nonmarket.period}
                    </p>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart
                        data={tenureData}
                        layout="vertical"
                        margin={{ top: 0, right: 16, left: 80, bottom: 0 }}
                      >
                        <XAxis
                          type="number"
                          tickFormatter={n => fmtShort(n)}
                          tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                          axisLine={false} tickLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          tick={{ fill: 'rgba(232,228,218,0.7)', fontSize: 13 }}
                          axisLine={false} tickLine={false}
                        />
                        <Tooltip
                          formatter={v => [fmt(v), 'Starts']}
                          contentStyle={{
                            background: '#1a1a2e',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 6,
                            fontSize: 13,
                            color: '#e8e4da',
                          }}
                          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        />
                        <Bar dataKey="value" fill={C.nonmarket} radius={[0, 3, 3, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : null;
              })()}

              {/* CMA table */}
              {nonmarket.cma_table?.length > 0 && (
                <>
                  <h2 className="font-serif text-[28px] text-ink-800 mb-6 mt-8">
                    Non-market starts by city
                  </h2>
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="border-b border-ink-300/60 text-ink-400 text-left">
                          <th className="pb-3 font-medium">City</th>
                          <th className="pb-3 font-medium text-right">Total</th>
                          <th className="pb-3 font-medium text-right">Rental</th>
                          <th className="pb-3 font-medium text-right">Condo</th>
                          <th className="pb-3 font-medium text-right">Co-op</th>
                          <th className="pb-3 font-medium text-right">Apartment units</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nonmarket.cma_table.map(row => (
                          <tr key={row.name} className="border-b border-ink-200/60 text-ink-600">
                            <td className="py-2.5">{row.name}</td>
                            <td className="py-2.5 text-right tabular font-semibold text-ink-800">{fmt(row.total)}</td>
                            <td className="py-2.5 text-right tabular">{row.rental > 0 ? fmt(row.rental) : '—'}</td>
                            <td className="py-2.5 text-right tabular">{row.condo > 0 ? fmt(row.condo) : '—'}</td>
                            <td className="py-2.5 text-right tabular">{row.coop > 0 ? fmt(row.coop) : '—'}</td>
                            <td className="py-2.5 text-right tabular">{row.total_apartment > 0 ? fmt(row.total_apartment) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mb-8">
                    <p className="text-[12px] text-ink-400 italic">
                      Only cities with non-market starts activity in {nonmarket.period} are shown.
                      Covers 18 CMAs surveyed by CMHC. Source:{' '}
                      <a
                        href={nonmarket.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-copper-600 hover:text-copper-500 inline-flex items-center gap-0.5"
                      >
                        {nonmarket.source_label} <ExternalLink size={10} />
                      </a>
                    </p>
                  </div>
                </>
              )}

              {/* What is non-market housing card */}
              <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-8 mb-12"
                   style={{ borderLeft: `4px solid ${C.nonmarket}` }}>
                <h3 className="font-semibold text-[15px] text-ink-800 mb-3">
                  What is non-market housing?
                </h3>
                <p className="text-[13px] text-ink-500 leading-relaxed">
                  Non-market housing starts are dwellings where units are not priced or allocated
                  through private market mechanisms. This includes social housing built by public,
                  non-profit, co-operative, or community-based organizations — as well as affordable
                  housing delivered under government programs. It encompasses rental, co-operative,
                  and sometimes homeownership units facilitated through charitable organizations.
                  CMHC has published this data quarterly since 2024, covering 18 major CMAs.
                </p>
              </div>
            </>
          ) : (
            /* Placeholder when no file has been uploaded yet */
            <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-12"
                 style={{ borderLeft: `4px solid ${C.nonmarket}` }}>
              <div className="kicker text-copper-600 mb-4">Non-market housing starts</div>
              <p className="text-[15px] text-ink-600 leading-relaxed max-w-xl">
                CMHC publishes this dataset quarterly as an Excel file. Upload the latest edition
                from the{' '}
                <a
                  href="https://www.cmhc-schl.gc.ca/professionals/housing-markets-data-and-research/housing-data/data-tables/housing-market-data/non-market-housing-starts"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-copper-600 hover:text-copper-500 inline-flex items-center gap-0.5"
                >
                  CMHC data tables page <ExternalLink size={11} />
                </a>
                {' '}to populate this section.
              </p>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════
            SECTION 3 — RENTAL MARKET
        ════════════════════════════════════════════════════ */}
        <div id="rental" className="scroll-mt-8">

          {/* Hero — vacancy rate */}
          {vacancy?.canada_latest && (
            <div
              className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-4"
              style={{ borderLeft: `4px solid ${C.vacancy}` }}
            >
              <div className="kicker text-copper-600 mb-6">
                Rental vacancy rate · Canada · {vacancy.canada_latest.year}
              </div>
              <div className="font-serif text-[72px] md:text-[100px] leading-[0.9] tracking-tight text-ink-800 tabular">
                {fmtPct(vacancy.canada_latest.vacancy_rate)}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <ChangeBadge value={vacancy.yoy_change} unit="%" positive="up" />
                {vacancy.yoy_change != null && (
                  <span className="text-[13px] text-ink-400">vs {vacancy.canada_prev?.year}</span>
                )}
                <span className="text-[13px] text-ink-400 ml-2">
                  · A healthy rental market is typically 3%+
                </span>
              </div>

              {avgRent?.canada_latest && (
                <div className="flex flex-wrap gap-10 mt-8 pt-8 border-t border-ink-300/40">
                  <StatPill
                    label={`Avg monthly rent · ${avgRent.canada_latest.year}`}
                    value={`$${fmt(avgRent.canada_latest.avg_rent)}`}
                  />
                  {avgRent.yoy_change != null && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span className="kicker text-ink-500" style={{ fontSize: 11 }}>Year-over-year change</span>
                      <span className="font-serif text-[28px] leading-none text-ink-700 tabular">
                        {fmtChange(avgRent.yoy_change, '$')}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Vacancy trend chart */}
          {vacancyChart.length > 0 && (
            <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-4">
              <h2 className="font-serif text-[22px] text-cream-100 mb-2">
                National vacancy rate over time
              </h2>
              <p className="text-[13px] text-cream-100/50 mb-8">
                Annual average rental vacancy rate, Canada,{' '}
                {vacancyChart[0]?.year}–{vacancyChart.at(-1)?.year}. CMHC survey conducted each October.
              </p>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={vacancyChart} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="vacGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.vacancy} stopOpacity={0.28} />
                      <stop offset="95%" stopColor={C.vacancy} stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="year"
                    tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 12 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tickFormatter={n => `${n.toFixed(1)}%`}
                    tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                    axisLine={false} tickLine={false} width={44}
                  />
                  <Tooltip content={<VacancyTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.08)', strokeWidth: 1 }} />
                  <Area
                    type="monotone"
                    dataKey="vacancy_rate"
                    stroke={C.vacancy}
                    strokeWidth={2}
                    fill="url(#vacGrad)"
                    dot={false}
                    activeDot={{ r: 4, fill: C.vacancy, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Avg rent trend chart */}
          {rentChart.length > 0 && (
            <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-4">
              <h2 className="font-serif text-[22px] text-cream-100 mb-2">
                Average monthly rent over time
              </h2>
              <p className="text-[13px] text-cream-100/50 mb-8">
                National average monthly rent for all bedroom types, Canada,{' '}
                {rentChart[0]?.year}–{rentChart.at(-1)?.year}
              </p>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={rentChart} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.vacancy} stopOpacity={0.28} />
                      <stop offset="95%" stopColor={C.vacancy} stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="year"
                    tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 12 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tickFormatter={n => `$${Math.round(n / 100) * 100}`}
                    tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                    axisLine={false} tickLine={false} width={56}
                  />
                  <Tooltip content={<RentTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.08)', strokeWidth: 1 }} />
                  <Area
                    type="monotone"
                    dataKey="avg_rent"
                    stroke={C.vacancy}
                    strokeWidth={2}
                    fill="url(#rentGrad)"
                    dot={false}
                    activeDot={{ r: 4, fill: C.vacancy, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* CMA table — vacancy + rent side by side */}
          {cmaRows.length > 0 && (
            <>
              <h2 className="font-serif text-[28px] text-ink-800 mb-6 mt-8">
                Rental market by city
              </h2>
              <div className="overflow-x-auto mb-12">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-ink-300/60 text-ink-400 text-left">
                      <th className="pb-3 font-medium">City</th>
                      <th className="pb-3 font-medium text-right">Vacancy rate</th>
                      <th className="pb-3 font-medium text-right">Avg monthly rent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cmaRows.map(row => (
                      <tr key={row.city} className="border-b border-ink-200/60 text-ink-600">
                        <td className="py-2.5">{row.city}</td>
                        <td className="py-2.5 text-right tabular">
                          {row.vacancy_rate != null
                            ? <span style={{ color: row.vacancy_rate < 3 ? '#D64949' : '#1D9E75' }}>
                                {fmtPct(row.vacancy_rate)}
                              </span>
                            : '—'}
                        </td>
                        <td className="py-2.5 text-right tabular">
                          {row.avg_rent ? `$${fmt(row.avg_rent)}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[12px] text-ink-400 mt-3 italic">
                  Vacancy rates below 3% (shown in red) indicate a tight rental market where rent
                  growth is typically above inflation. Source: CMHC Rental Market Survey, annual.
                </p>
              </div>
            </>
          )}
        </div>

        {/* ════════════════════════════════════════════════════
            SECTION 4 — HOMELESSNESS
        ════════════════════════════════════════════════════ */}
        <div id="homelessness" className="scroll-mt-8">

          {homelessness && (
            <div
              className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-4"
              style={{ borderLeft: `4px solid ${C.shelter}` }}
            >
              <div className="kicker text-copper-600 mb-6">
                Emergency shelter use · Canada · {homelessness.year}
              </div>
              <div className="font-serif text-[72px] md:text-[100px] leading-[0.9] tracking-tight text-ink-800 tabular">
                {fmt(homelessness.shelter_users)}
              </div>
              <p className="text-[14px] text-ink-500 mt-2">
                People who spent at least one night in an emergency shelter in {homelessness.year}
              </p>

              <div className="flex flex-wrap gap-10 mt-8 pt-8 border-t border-ink-300/40">
                <StatPill
                  label="Experiencing chronic homelessness"
                  value={fmt(homelessness.chronic_count)}
                />
                <StatPill
                  label="Chronically homeless share"
                  value={fmtPct(homelessness.chronic_pct)}
                />
              </div>
            </div>
          )}

          {/* Reaching Home program cards */}
          {homelessness && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div
                className="bg-cream-50 border border-ink-300/60 rounded-sm p-8"
                style={{ borderLeft: `4px solid ${C.shelter}` }}
              >
                <div className="kicker text-copper-600 mb-3">Reaching Home investment</div>
                <div className="font-serif text-[40px] leading-none text-ink-800 tabular mb-2">
                  ${homelessness.reaching_home_investment_b}B
                </div>
                <p className="text-[13px] text-ink-500 leading-relaxed">
                  Federal investment over nine years (2019–2028) through Reaching Home: Canada's
                  Homelessness Strategy, including $1B announced in Budget 2024.
                </p>
              </div>

              <div
                className="bg-cream-50 border border-ink-300/60 rounded-sm p-8"
                style={{ borderLeft: `4px solid ${C.shelter}` }}
              >
                <div className="kicker text-copper-600 mb-3">People helped since 2019</div>
                <div className="font-serif text-[40px] leading-none text-ink-800 tabular mb-2">
                  {fmt(homelessness.people_housed)}+
                </div>
                <p className="text-[13px] text-ink-500 leading-relaxed">
                  Assisted in attaining more stable housing. An additional {fmt(homelessness.people_prevention)}+
                  received prevention services such as short-term rental assistance or landlord mediation.
                </p>
              </div>
            </div>
          )}

          {/* Context note */}
          {homelessness && (
            <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-8 mb-12">
              <h3 className="font-semibold text-[15px] text-ink-800 mb-3">
                What these numbers measure — and don't
              </h3>
              <p className="text-[13px] text-ink-500 leading-relaxed mb-3">
                The National Shelter Study tracks people who use emergency shelters in the HIFIS
                (Homeless Individuals and Families Information System). It does not count people
                sleeping rough, those in transitional housing, domestic violence shelters, or
                refugee/immigrant shelters — meaning the true number of people experiencing
                homelessness is higher.
              </p>
              <p className="text-[13px] text-ink-500 leading-relaxed">
                Chronic homelessness is defined as shelter stays of 6+ months in a year (acute
                chronicity) or shelter stays in each of the past three years (prolonged instability).
                In {homelessness.year}, {fmtPct(homelessness.chronic_pct)} of shelter users
                met one or both criteria.
              </p>
            </div>
          )}
        </div>

        {/* ── Sources footer ── */}
        <div className="mt-10 text-[14px] text-ink-500 leading-relaxed">
          <strong className="text-ink-700">Sources:</strong>{' '}
          Statistics Canada Tables 34-10-0126-01 (housing starts, annual),
          34-10-0127-01 (vacancy rates, CMA) and 34-10-0132-01 (average rents, CMA) — via CMHC
          Starts and Completions Survey and Rental Market Survey.{' '}
          Infrastructure Canada — National Shelter Study {homelessness?.year ?? '2024'} and
          Reaching Home program reporting.{' '}
          <a
            href="https://www150.statcan.gc.ca/t1/tbl1/en/tv.action?pid=3410012601"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-copper-600 hover:text-copper-500 ml-1"
          >
            StatCan housing starts <ExternalLink size={11} />
          </a>
          {' ·'}
          <a
            href="https://www150.statcan.gc.ca/t1/tbl1/en/tv.action?pid=3410012701"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-copper-600 hover:text-copper-500 ml-1"
          >
            Vacancy rates <ExternalLink size={11} />
          </a>
          {' ·'}
          <a
            href="https://housing-infrastructure.canada.ca/homelessness-sans-abri/reports-rapports/data-shelter-2024-donnees-refuge-eng.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-copper-600 hover:text-copper-500 ml-1"
          >
            National Shelter Study <ExternalLink size={11} />
          </a>
        </div>
        <div className="mt-4 text-[13px] text-ink-400 italic max-w-2xl">
          Housing starts data updated annually. Rental Market Survey conducted each October and
          published in December. Shelter study data updated annually by Infrastructure Canada.
        </div>
      </main>

      <Footer />
    </div>
  );
}
